import { computed, toValue, type Ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { withUtmParams, type UtmParams } from '@/utils/utm'

type MaybeRefOrGetter<T> = T | Ref<T> | (() => T)

export interface CtaOptions {
  type: 'internal' | 'external' | 'email' | 'phone' | 'scroll'
  to?: string
  text: string
  icon?: string
  iconPosition?: 'left' | 'right'
  appearance?: 'button' | 'link'
  color?: 'primary' | 'secondary' | 'accent' | 'neutral'
  targetEl?: Ref<HTMLElement | null> | string
  utm?: UtmParams
}

export function useCta(options: MaybeRefOrGetter<CtaOptions>) {
  const opt = computed(() => toValue(options))

  const isInternal = computed(() => opt.value.type === 'internal')
  const isExternal = computed(() => opt.value.type === 'external')
  const isEmail = computed(() => opt.value.type === 'email')
  const isPhone = computed(() => opt.value.type === 'phone')
  const isScroll = computed(() => opt.value.type === 'scroll')

  const href = computed(() => {
    if (isExternal.value && opt.value.to) {
      return opt.value.utm ? withUtmParams(opt.value.to, opt.value.utm) : opt.value.to
    }

    return isEmail.value ? `mailto:${opt.value.to}` :
      isPhone.value ? `tel:${opt.value.to}` :
      undefined
  })

  const routerTo = computed<RouteLocationRaw | undefined>(() =>
    isInternal.value ? opt.value.to : undefined
  )

  const target = computed(() => (isExternal.value ? '_blank' : undefined))
  const rel = computed(() => (isExternal.value ? 'noopener' : undefined))
  const appearance = computed(() => opt.value.appearance ?? 'button')
  const iconPosition = computed(() => opt.value.iconPosition ?? 'right')

  const colorClass = computed(() =>
    appearance.value === 'link'
      ? opt.value.color === 'secondary'
        ? 'link-ui--secondary'
        : opt.value.color === 'accent'
          ? 'link-ui--accent'
          : opt.value.color === 'neutral'
            ? 'link-ui--neutral'
            : 'link-ui--primary'
      : opt.value.color === 'secondary'
        ? 'btn-ui--secondary'
        : opt.value.color === 'accent'
          ? 'btn-ui--secondary'
          : opt.value.color === 'neutral'
            ? 'btn-ui--muted'
            : 'btn-ui--primary'
  )

  function onClick(e: Event) {
    if (!isScroll.value) return
    e.preventDefault()

    let el: HTMLElement | null = null
    const targetEl = opt.value.targetEl
    if (typeof targetEl === 'string') {
      el = document.querySelector(targetEl)
    } else if (targetEl?.value) {
      el = targetEl.value
    }
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return {
    isInternal,
    isExternal,
    isEmail,
    isPhone,
    isScroll,
    href,
    routerTo,
    target,
    rel,
    appearance,
    iconPosition,
    colorClass,
    text: computed(() => opt.value.text),
    icon: computed(() => opt.value.icon),
    onClick,
  }
}
